import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import Styles from './TemplateButton.module.scss'
import { forwardRef, useState } from 'react'
import { VisualElement } from 'api/clients/VisualElementClient'
import { useObjectRef } from '@react-aria/utils'
import { getTemplatePreviewCloudinaryUrl } from 'components/Design/utils/getTemplatePreviewCloudinaryUrl'
import { WIDE_ASPECT_RATIO } from '../TemplateCarousel/constants'
import { Text } from 'blaze-ui/Text/Text'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { getTemplateDisplayName } from 'components/Templates/TemplatesModalV2/components/TemplatesGrid'

interface Props {
  template: VisualElement
  onPress: () => void
  aspectRatio: number
  itemWidth?: number
  itemHeight?: number
  ignoreAspectRatio?: boolean
  withName?: boolean
}

export const TemplateButton = observer(
  forwardRef<HTMLDivElement, Props>(
    ({ template, onPress, aspectRatio, itemWidth, itemHeight, ignoreAspectRatio, withName = true }, fRef) => {
      const ref = useObjectRef(fRef)
      const isWide = aspectRatio > WIDE_ASPECT_RATIO
      const [isLoaded, setIsLoaded] = useState(false)

      const url = getTemplatePreviewCloudinaryUrl(template.preview_url, { height: itemHeight, width: itemWidth })
      const name = getTemplateDisplayName(template.name)

      return (
        <div
          className={cx(Styles.templateButton, {
            [Styles.wide]: aspectRatio > 1.5,
            [Styles.ignoreAspectRatio]: ignoreAspectRatio,
          })}
          ref={ref}
          role="button"
          tabIndex={0}
          aria-label={`Select template: ${name}`}
          onClick={onPress}
        >
          <div
            className={cx(Styles.imageContainer, {
              [Styles.wide]: aspectRatio > 1.5,
            })}
            style={{ aspectRatio: ignoreAspectRatio ? undefined : aspectRatio }}
          >
            {!isLoaded && <LoadingShimmerer className={cx(Styles.shimmerer, { [Styles.wide]: isWide })} />}

            <img
              key={template.id}
              src={url}
              alt={name}
              className={cx({ [Styles.wide]: isWide })}
              onLoad={() => setIsLoaded(true)}
              style={{ display: isLoaded ? 'block' : 'none' }}
            />
          </div>
          {withName && (
            <div className={Styles.textContainer}>
              <Text variant="smallList" lineClamp={2}>
                {name}
              </Text>
            </div>
          )}
        </div>
      )
    },
  ),
)
